import * as React from 'react';
import type { HorizontalScrollOverflowState } from '../types/use-horizontal-scroll-overflow-types';

const INITIAL_OVERFLOW_STATE: HorizontalScrollOverflowState = {
  canScrollLeft: false,
  canScrollRight: false,
  hasOverflow: false,
};

function measureHorizontalOverflow(element: HTMLElement): HorizontalScrollOverflowState {
  const maxScrollLeft = element.scrollWidth - element.clientWidth;
  const canScrollLeft = element.scrollLeft > 0;
  const canScrollRight = maxScrollLeft > 1 && element.scrollLeft < maxScrollLeft - 1;
  const hasOverflow = maxScrollLeft > 1;

  return { canScrollLeft, canScrollRight, hasOverflow };
}

export function useHorizontalScrollOverflow(
  scrollRef: React.RefObject<HTMLElement | null>
): HorizontalScrollOverflowState {
  const [overflow, setOverflow] = React.useState<HorizontalScrollOverflowState>(
    INITIAL_OVERFLOW_STATE
  );

  const remeasure = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      setOverflow(INITIAL_OVERFLOW_STATE);
      return;
    }

    setOverflow(measureHorizontalOverflow(element));
  }, [scrollRef]);

  React.useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    remeasure();

    element.addEventListener('scroll', remeasure, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      remeasure();
    });
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(() => {
      remeasure();
    });
    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });

    return () => {
      element.removeEventListener('scroll', remeasure);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [remeasure, scrollRef]);

  return overflow;
}
